import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { PaymentMode } from "src/models/InstallmentPayment.model";
import {
  TransactionListPropType,
  TransactionType,
} from "src/models/Transactions.model";
import {
  setDateFilter,
  setDateFilterForPaymentDate,
  setFilterData,
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/TransactionsSlice";
import { RootState } from "src/redux/store";
import { useGetAdminTransactionsQuery } from "src/services/TransactionsServices";
import TransactionFilterDialogWrapper from "../FilterDialog/TransactionFilterDialogWrapper";
import TransactionsListing from "./TransactionsListing";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";

const TransactionsListingWrapper = () => {
  const dispatch = useDispatch();
  const [isOpenFilterDialog, setIsOpenFilterDialog] = useState(false);
  const [upiAmount, setUpiAmount] = useState<any>(0);
  const [cashAmount, setCashAmount] = useState<any>(0);
  const [netBankingAmount, setNetBankingAmount] = useState<any>(0);

  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isTableLoading,
    dateFilter,
    filterData,
    dateFilterForPaymentDate,
  } = useSelector((state: RootState) => state.transactions);
  const { data, isLoading, isFetching } = useGetAdminTransactionsQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["studentLabel"],
    page: page,
    filterBy: [
      {
        fieldName: "type",
        value: filterData?.type?.map((data: any) => data?.value),
      },
      {
        fieldName: "mode",
        value: filterData?.mode?.map((data: any) => data?.value),
      },
    ],
    dateFilter: {
      dateFilterKey: "createdAt",
      startDate: dateFilter?.startDate,
      endDate: dateFilter?.endDate,
    },
    dateFilterForPaymentDate: {
      dateFilterKey: "paymentDate",
      startDate: dateFilterForPaymentDate?.startDate,
      endDate: dateFilterForPaymentDate?.endDate,
    },
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Date - Time",
      extraClasses: "py-1",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatedDateTimeIntoIst(row.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "studentName",
      headerName: "student Name",
      flex: "flex-[0.75_0.75_0%]",
      extraClasses: "",
      renderCell: (row: TransactionListPropType) => {
        return <span>{row?.studentName}</span>;
      },
    },
    {
      field: "paymentDate",
      headerName: "Payment-Date",
      extraClasses: "py-1",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div className="text-slate-700 font-medium">
          {formatedDateTimeIntoIst(row.paymentDate, "DD MMM yyyy")}
        </div>
      ),
    },
    {
      field: "type",
      headerName: "Related To",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row: TransactionListPropType) => {
        return <span>{TransactionType[row?.type] || ""}</span>;
      },
    },
    {
      field: "mode",
      headerName: "Payment Mode",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row: TransactionListPropType) => {
        return <span>{PaymentMode[row?.mode] || ""}</span>;
      },
    },
    {
      field: "accountLabel",
      headerName: "Account",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "",
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: "flex-[0.75_0.75_0%]",
      extraClasses: "",
      renderCell: (row: TransactionListPropType) => {
        return <span>&#8377; {row?.amount}</span>;
      },
    },
    {
      field: "remark",
      headerName: "Remark",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses:
        "py-2  overflow-scroll min-w-[300px] max-h-[100px]  max-w-[300px]",
      renderCell: (row: TransactionListPropType) => (
        <p className="text-[13px] text-balance"> {row?.remark} </p>
      ),
    },
  ];

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      setNetBankingAmount(
        data?.groupedAmount?.map(
          (el: any) =>
            el?.paymentMode === "BANK_TRANSFER" && `${el?.totalAmount}`
        )
      );
      setCashAmount(
        data?.groupedAmount?.map(
          (el: any) => el?.paymentMode === "CASH" && `${el?.totalAmount}`
        )
      );
      setUpiAmount(
        data?.groupedAmount?.map(
          (el: any) => el?.paymentMode === "UPI" && `${el?.totalAmount}`
        )
      );
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleFilter = ({
    startDate,
    endDate,
    transactionType,
    mode,
    accountId,
    paymentStartDate,
    paymentEndDate,
  }: {
    startDate: string;
    paymentEndDate: string;
    paymentStartDate: string;
    endDate: string;
    transactionType: string[];
    mode: string[];
    accountId: string[];
  }) => {
    dispatch(
      setDateFilterForPaymentDate({
        startDate: paymentStartDate,
        endDate: paymentEndDate,
      })
    );

    dispatch(
      setDateFilter({
        startDate,
        endDate,
      })
    );
    dispatch(
      setFilterData({
        transactionType,
        mode,
        accountId,
      })
    );
  };

  return (
    <div>
      <TransactionsListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.TRANSACTIONS,
          actionName: UserModuleActionTypes.LIST,
          columns: columns,
        })}
        rows={items}
        upiAmount={upiAmount}
        cashAmount={cashAmount}
        netBankingAmount={netBankingAmount}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onFilterClick={() => setIsOpenFilterDialog(true)}
      />
      {isOpenFilterDialog ? (
        <TransactionFilterDialogWrapper
          onClose={() => setIsOpenFilterDialog(false)}
          formValues={{
            dateFilter: dateFilter,
            transactionType: filterData?.type || [],
            account: filterData?.accountId || [],
            mode: filterData?.mode || [],
            dateFilterForPaymentDate: dateFilterForPaymentDate,
          }}
          handleFilter={handleFilter}
        />
      ) : null}
    </div>
  );
};

export default TransactionsListingWrapper;
